import NegociaService from '@/services/negocieja.service.js'
import moment from 'moment'

const initialState = {
  campanhas: [], 
  politicas: [],     
  publicos: {          
    disponiveis: [],
    selecionados: []
  },
  pagamentos: {
    disponiveis: [],
    selecionados: []
  },
  termo: {
    uuid: null,
    JSON: null,
    url: null,
    contrato: null,
    assinatura: null
  },
  proposta: null,
  formaEscolhida: {
    idNegocieJaFormaPagamento: null,
    nome: null,
    valorTotalAtualizado: 0
  },
  parametros: {
    entradaTipo: '',
    parcelaTipo: '',
    entradaPercent: null,
    qtdParcelasEntrada: 1,
    parcelas: 1
  },
  pagamento: {
    entrada: {
      cartao: {
        numero: null,
        nome: null,
        cvv: null,
        validade: null
      }
    },
    parcelas: {
      cartao: {
        numero: null,
        nome: null,
        cvv: null,
        validade: null
      }
    }
  },
  loading: {
    campanhas: false,
    politicas: false,
    publicos: false,
    pagamentos: false
  },
  checkout: null,
  parcelas: null
}

export const negocieja = {
  namespaced: true,

  state: { ...initialState },

  mutations: {
    setFormaEscolhida(state, payload) {
      state.formaEscolhida = {
        idNegocieJaFormaPagamento: payload?.idNegocieJaFormaPagamento ?? null,
        nome: payload?.nome ?? null,
        valorTotalAtualizado: Number(payload?.valorTotalAtualizado ?? payload?.valorFinalCobrado ?? 0)
      }
    },
    setParametrosPagamento(state, payload) {
      state.parametros = {
        entradaTipo: payload?.entradaTipo ?? '',
        parcelaTipo: payload?.parcelaTipo ?? '',
        entradaPercent: payload?.entradaPercent ?? null,
        qtdParcelasEntrada: payload?.qtdParcelasEntrada ?? 1,
        parcelas: payload?.parcelas ?? 1
      }
    },
    setCampanhas(state, list) {
      state.campanhas = list
    },
    setPoliticas(state, list) {
      state.politicas = list
    },
    setPublicosDisponiveis(state, list) {
      state.publicos.disponiveis = list
    },
    setPublicosSelecionados(state, list) {
      state.publicos.selecionados = list
    },
    setPagamentosDisponiveis(state, list) {
      state.pagamentos.disponiveis = list
    },
    setPagamentosSelecionados(state, list) {
      state.pagamentos.selecionados = list
    },
    setLoading(state, { key, value }) {
      if (Object.prototype.hasOwnProperty.call(state.loading, key)) {
        state.loading[key] = value
      }
    },
    resetState(state) {
      Object.assign(state, initialState)
    }
  },

  actions: {
    async fetchCampanhas({ commit }) {
      commit('setLoading', { key: 'campanhas', value: true })
      try {
        const res = await NegociaService.getCampanhas()
        commit('setCampanhas', res.data)
      } finally {
        commit('setLoading', { key: 'campanhas', value: false })
      }
    },

    async fetchPoliticas({ commit }) {
      commit('setLoading', { key: 'politicas', value: true })
      try {
        const res = await NegociaService.getPoliticas()
        const list = res.data.map(p => ({
          ...p,
          active: false,
          loadingDetails: false,
          activeTab: 'bloqueios',
          details: null
        }))
        commit('setPoliticas', list)
      } finally {
        commit('setLoading', { key: 'politicas', value: false })
      }
    },

    async fetchPublicos({ commit }, selected = []) {
      commit('setLoading', { key: 'publicos', value: true })
      try {
        const res = await NegociaService.getPublicos()
        const unique = Array.from(
          new Map(res.data.map(p => [p.idPublico, p])).values()
        )
        const idsSel = new Set(selected.map(p => p.idPublico))
        commit('setPublicosSelecionados', [...selected])
        commit(
          'setPublicosDisponiveis',
          unique.filter(p => !idsSel.has(p.idPublico))
        )
      } finally {
        commit('setLoading', { key: 'publicos', value: false })
      }
    },

    salvarFormaEscolhida({ commit }, forma) {
      commit('setFormaEscolhida', forma)
      try {
        localStorage.setItem('negocieja_forma_escolhida', JSON.stringify({
          idNegocieJaFormaPagamento: forma?.idNegocieJaFormaPagamento ?? null,
          nome: forma?.nome ?? null,
          valorTotalAtualizado: Number(forma?.valorTotalAtualizado ?? forma?.valorFinalCobrado ?? 0)
        }))
      } catch (e) {
          console.warn('Falha ao persistir forma escolhida no localStorage', e)
      }
      return Promise.resolve(true)
    },

    salvarParametrosPagamento({ commit }, params) {
      commit('setParametrosPagamento', params)
      try {
        localStorage.setItem('negocieja_parametros', JSON.stringify({
          entradaTipo: params?.entradaTipo ?? '',
          parcelaTipo: params?.parcelaTipo ?? '',
          entradaPercent: params?.entradaPercent ?? null,
          qtdParcelasEntrada: params?.qtdParcelasEntrada ?? 1,
          parcelas: params?.parcelas ?? 1
        }))
      } catch (e) {
          console.warn('Falha ao persistir forma escolhida no localStorage', e)
      }
      return Promise.resolve(true)
    },
    
    montarPayload({ state }, { cpf, mencodigos, idCampanha = null, idPolitica = null }) {
      const totalOriginal = Number(state?.proposta?.valorTotalOriginal ?? 0)
      const totalFinal = Number(state?.proposta?.valorFinalCobrado ?? 0)

      const params = state.parametros
      const total = Number(state.formaEscolhida?.valorTotalAtualizado ?? totalFinal)

      const temEntrada = params.entradaPercent > 0
      const valorEntradaTotal = temEntrada ? (params.entradaPercent / 100) * total : 0
      const qtdParcelasEntrada = temEntrada ? Math.max(1, Number(params.qtdParcelasEntrada || 1)) : 0
      const valorParcelaEntrada = qtdParcelasEntrada > 0 ? valorEntradaTotal / qtdParcelasEntrada : 0
      
      const baseParcelamento = temEntrada ? total - valorEntradaTotal : total
      const qtdParcelas = Math.max(1, Number(params.parcelas || 1))
      const valorParcela = baseParcelamento / qtdParcelas

      const entradaParcelas = []
      if (temEntrada) {
        for (let i = 0; i < qtdParcelasEntrada; i++) {
          entradaParcelas.push({
            tipoArrecadacao: 'entrada',
            parcela: i + 1,
            vencimento: moment().add(i, 'month').format('YYYY-MM-DD'),
            valor: valorParcelaEntrada
          })
        }
      }

      const parcelas = []
      for (let i = 0; i < qtdParcelas; i++) {
        parcelas.push({
          tipoArrecadacao: 'parcela',
          parcela: i + 1,
          vencimento: moment().add(i + 1, 'month').format('YYYY-MM-DD'),
          valor: valorParcela
        })
      }

      const payload = {
        cpf_responsavel_financeiro: cpf,
        mencodigos,
        valor: {
          totalOriginal,
          totalFinal: total
        },
        idNegocieJaCampanha: idCampanha,
        idNegocieJaPolitica: idPolitica,
        formaPagamento: {
          idFormaPagamento: state.formaEscolhida?.idNegocieJaFormaPagamento,
          percentualEntrada: params.entradaPercent,
          qtdParcelas,
          qtdParcelasEntrada
        },
        entrada: {
          pix: params.entradaTipo === 'pix' ? 1 : null,
          cartao: params.entradaTipo === 'cartao' ? 1 : null,
          boleto: params.entradaTipo === 'boleto' ? 1 : null,
          parcelas: entradaParcelas
        },
        parcelas: {
          pix: params.parcelaTipo === 'pix' ? 1 : null,
          cartao: params.parcelaTipo === 'cartao' ? 1 : null,
          boleto: params.parcelaTipo === 'boleto' ? 1 : null,
          parcelas
        }
      }

        return payload
    },

    async fetchPagamentos({ commit }, selected = []) {
      commit('setLoading', { key: 'pagamentos', value: true })
      try {
        const res = await NegociaService.getPagamentos()
        const unique = Array.from(
          new Map(res.data.map(p => [p.idPagamento, p])).values()
        )
        const idsSel = new Set(selected.map(p => p.idPagamento))
        commit('setPagamentosSelecionados', [...selected])
        commit(
          'setPagamentosDisponiveis',
          unique.filter(p => !idsSel.has(p.idPagamento))
        )
      } finally {
        commit('setLoading', { key: 'pagamentos', value: false })
      }
    },

    get () {
      return Promise.resolve(initialState)
    },
  }
}
